import { Pipe, PipeTransform, Inject, LOCALE_ID } from "@angular/core";
import { locale } from "devextreme/localization";
import { FeatureFlagsService } from "../shared/services/feature-flags.service";
import { UtilitiesService } from "../shared/components/framework/services/utilities.service";

// requestTypes whose loan-amount-grid bookName column is captioned "Property" rather than "Book Name"
export const BOOK_NAME_PROPERTY_REQUEST_TYPES = new Set([
  'GPF Alteration',
  'Lease Consent',
  'REF Lease Consent',
  'Loan Event - Amendment of Security',
  'REF Amendment of Security',
  'Loan Event - Change of Control',
  'REF Change of Control',
]);

@Pipe({
  standalone: false, name: 'filedatetransform' })
export class FileDatePipe<TArgs, TReturn> implements PipeTransform {
  transform(value: any) {
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date;
  }
}

@Pipe({
  standalone: false,
  name: 'capitalizefirst'
})
export class CapitalizeFirstPipe<TArgs, TReturn> implements PipeTransform {
  transform(value: string): string {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}

@Pipe({
  standalone: false,
  name: 'formatandcapitalize'
})
export class FormatAndCapitalizePipe implements PipeTransform {
  transform(value: string): string {
    if (!value) return value;
    return value
      .replace(/([A-Z])/g, ' $1')
      .trim()  // Remove leading spaces
      .replace(/^./, char => char.toUpperCase());
  }

}

@Pipe({
  standalone: false,
  name: 'formatbooleannumbersdates'
})
export class FormatBooleanNumersDatesPipe implements PipeTransform {
  constructor(@Inject(LOCALE_ID) private locale: string) {}

  transform(value: any): any {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    else if (typeof value === 'number') {
      // Use the live DevExtreme region locale, not the build-time Angular LOCALE_ID
      return value.toLocaleString(locale());
    }
    else if (typeof value === 'string' && this.isValidDateString(value)) {
      const date = new Date(value);
      return date.toLocaleDateString(locale());
    }
    return value;
  }

  private isValidDateString(value: string): boolean {
    // Check if the string looks like a date format
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO datetime
      /^\d{1,2}\/\d{1,2}\/\d{4}$/, // MM/DD/YYYY or M/D/YYYY
      /^\d{1,2}-\d{1,2}-\d{4}$/, // MM-DD-YYYY or M-D-YYYY
      /^\d{4}\/\d{1,2}\/\d{1,2}$/, // YYYY/MM/DD or YYYY/M/D
    ];

    // First check if it matches common date patterns
    const matchesPattern = datePatterns.some(pattern => pattern.test(value.trim()));

    if (!matchesPattern) {
      return false;
    }

    // Then verify it's actually a valid date
    const date = new Date(value);
    return !isNaN(date.getTime());
  }
}

@Pipe({
  standalone: false,
  name: 'formattaskdata'
})
export class FormatTaskDataPipe implements PipeTransform {
  constructor(private readonly featureFlagsService: FeatureFlagsService) {}

  transform(value: any, existingColumns?: string[] | string, deals?: any[], searchText?: string): string {
    if (value === null || value === undefined) {
      return '';
    }

    // The input is always a JSON string
    if (typeof value === 'string') {
      try {
        const parsedData = JSON.parse(value);

        // Get existing column names for filtering
        let columnNames: string[] = [];
        if (existingColumns) {
          if (typeof existingColumns === 'string') {
            // If a single column name is passed
            columnNames = [existingColumns];
          } else if (Array.isArray(existingColumns)) {
            // If an array of column names is passed
            columnNames = existingColumns;
          }
        }

        // Normalize column names for comparison (convert to lowercase for case-insensitive comparison)
        const normalizedColumnNames = columnNames.map(col => col.toLowerCase());

        // Check if we have both currency and amount for special formatting
        const hasCurrency = 'currency' in parsedData;
        const hasAmount = 'amount' in parsedData || 'transferAmount' in parsedData;
        const currencyCode = hasCurrency ? parsedData.currency : null;

        // Filter out entries that match existing column names or are explicitly excluded
        const entries = Object.entries(parsedData).filter(([key, val]) => {
          if (key.startsWith('_')) {
            return false;
          }

          // Initiation-form description is distinct data from the grid's own hidden Description column
          if (key.toLowerCase() === 'description') {
            return true;
          }

          if (key === 'facilityIdDisplay') {
            return false;
          }

          // An optional facility left unselected is not worth a blank row
          if (key === 'facilityId' && (val == null || val === '' || (Array.isArray(val) && val.length === 0))) {
            return false;
          }

          // Exclude responsiblePersonId and responsibleReviewerId
          if (key === 'responsiblePersonId' || key === 'responsibleReviewerId') {
            return false;
          }

          // ERF Request's loanNumber is always blank filler data; hide it rather than show an empty row.
          // Unvalidated Leases - Ongoing Management is not deal specific, so it has no loan number at all.
          if (key === 'loanNumber' && (this.isErfRequest(parsedData) || this.isUnvalidatedLeasesOngoingManagementRequest(parsedData) || this.isNoticeRequest(parsedData))) {
            return false;
          }


          // Convert camelCase key to various formats for comparison
          const keyLower = key.toLowerCase();
          const keyTitleCase = this.formatKey(key).toLowerCase();
          const keySpaced = key.replace(/([A-Z])/g, ' $1').trim().toLowerCase();

          // Debug: Log the key variations and column names for troubleshooting
          // console.log('Key variations:', { original: key, keyLower, keyTitleCase, keySpaced });
          // console.log('Column names to check against:', normalizedColumnNames);

          // Check if any of these variations match existing column names
          const isMatch = normalizedColumnNames.some(colName =>
            colName === keyLower ||
            colName === keyTitleCase ||
            colName === keySpaced ||
            colName.replace(/\s+/g, '') === keyLower ||
            colName.replace(/\s+/g, '').toLowerCase() === key.toLowerCase() ||
            this.isFieldMappedToColumn(key, colName)
          );

          // Debug: Log if this key is being filtered out
          if (isMatch) {
            // console.log(`Filtering out key: ${key} (matched column)`);
          }

          return !isMatch;
        });

        // If no entries remain after filtering, return empty string
        if (entries.length === 0) {
          return '';
        }

        const formattedEntries = entries.map(([key, val]) => {
          // Format the key to be more readable (convert camelCase to Title Case)
          const formattedKey = this.getEntryLabel(key, parsedData);
          const displayValue = this.getTaskDataDisplayValue(key, val, parsedData);

          // Special handling for amount when currency is present
          let formattedValue: string;
          if ((key === 'amount' || key === 'transferAmount') && hasCurrency && currencyCode && typeof val === 'number') {
            formattedValue = this.formatCurrencyAmount(val, currencyCode);
          } else {
            formattedValue = this.formatValue(displayValue, parsedData, key, deals);
          }

          // Loan Amounts arrays are rendered bare (no "Loan Amounts:" label or parens,
          // no Loan Number) since the loan number is already shown elsewhere in the grid.
          if (key === 'loanAmounts' && Array.isArray(displayValue)) {
            const bareValue = formattedValue.replace(/^\(/, '').replace(/\)$/, '');
            return this.highlightValue(bareValue, searchText);
          }

          // Long values wrap (via CSS) rather than truncate, so the tooltip-on-truncation
          // behaviour previously here is no longer needed.
          return `<strong>${formattedKey}</strong>: ${this.highlightValue(formattedValue, searchText)}`;
        });

        // No manual line-wrapping: let the cell's own CSS word-wrap reflow this at render time,
        // so it fills the available width instead of breaking at a fixed character count.
        return formattedEntries.join(', ');
      } catch (error) {
        // If JSON parsing fails, return the original string
        return String(value);
      }
    }

    // Fallback for non-string inputs
    return String(value);
  }

  private formatKey(key: string): string {
    // Convert camelCase to Title Case
    return key
      .replace(/([A-Z])/g, ' $1') // Insert space before uppercase letters
      .replace(/^./, char => char.toUpperCase()) // Capitalize first letter
      .trim();
  }

  // Wraps search-term matches in the same CSS class DevExtreme's native search-text highlighter
  // uses, so highlighted values look identical to natively-highlighted columns. Only ever called
  // on the formatted VALUE text, never on formattedKey, so JSON property labels stay unhighlighted.
  private highlightValue(text: string, searchText?: string): string {
    if (!searchText || !text) {
      return text;
    }
    const escapedTerm = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    return text.replace(regex, '<span class="dx-datagrid-search-text">$1</span>');
  }

  private getEntryLabel(key: string, parsedData: any): string {
    // The loan-amount-grid's bookName column is captioned "Property" on these templates
    // (each has a '(REF) <name>' sibling requestType used by the REF team's own sub-task form)
    if (key === 'bookName' && BOOK_NAME_PROPERTY_REQUEST_TYPES.has(parsedData?.requestType)) {
      return 'Property';
    }

    if (key === 'bookName' && parsedData?.requestType === 'GPF Default') {
      return 'Property Name';
    }

    // ERF Request's facilityId is really the selected deal, not a loan facility reference
    if (key === 'facilityId' && this.isErfRequest(parsedData)) {
      return 'Deal Name';
    }

    // Onboarding Tenancy shows this bare, without the "Deal" qualifier (see formatArrayValue)
    if (key === 'selectedDealLoanNumbers' && this.isOnboardingTenancyRequest(parsedData)) {
      return 'Selected Loan Numbers';
    }

    return this.formatKey(key);
  }

  private isErfRequest(parsedData: any): boolean {
    return (parsedData?.requestType || '').toString().toUpperCase() === 'ERF REQUEST';
  }

  // Notice is raised against a deal, not a loan, so the (always blank) loan number is not shown.
  private isNoticeRequest(parsedData: any): boolean {
    return (parsedData?.requestType || '').toString().trim().toUpperCase() === 'NOTICE';
  }

  private isUnvalidatedLeasesOngoingManagementRequest(parsedData: any): boolean {
    return /^unvalidated\s+leases\s*-\s*ongoing\s+management/i.test((parsedData?.requestType || '').toString().trim());
  }

  // Tenancy requests are persisted under variant names too (e.g. "Onboarding - Tenancy - Proda Yes"),
  // and under older/renamed names ("Onboarding Tenancy", "Aviva Onboarding - Tenancy").
  private isOnboardingTenancyRequest(parsedData: any): boolean {
    return /^(aviva\s+)?onboarding\s*-?\s*tenancy/i.test((parsedData?.requestType || '').toString().trim());
  }

  private getTaskDataDisplayValue(key: string, value: any, parsedData: any): any {
    if (key === 'facilityId') {
      const displayValue = parsedData?.facilityIdDisplay;
      if (Array.isArray(displayValue)) {
        const values = displayValue
          .filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
          .map((item: string) => item.trim());
        const joined = UtilitiesService.dedupeFacilityDisplayValues(values).join(', ');
        if (joined) {
          return joined;
        }
      } else if (typeof displayValue === 'string' && displayValue.trim()) {
        return displayValue.trim();
      }
    }

    // Stored as a 0-1 fraction (e.g. 0.155); display on the same 0-100 scale as the input field
    if (key === 'reinvestmentMargin' && typeof value === 'number') {
      return Math.round(value * 10000) / 100;
    }

    // Some request types (e.g. GPF Default) leave the top-level loanNumber blank, with the
    // real value(s) only populated per-entry on loanAmounts
    if (key === 'loanNumber' && !value && Array.isArray(parsedData?.loanAmounts)) {
      const loanNumbers = Array.from(new Set(
        parsedData.loanAmounts
          .map((item: any) => item?.loanNumber)
          .filter((ln: unknown): ln is string => typeof ln === 'string' && ln.trim().length > 0)
      ));
      if (loanNumbers.length) {
        return loanNumbers.join(', ');
      }
    }

    return value;
  }

  private formatValue(value: any, parsedData?: any, currentKey?: string, deals?: any[]): string {
    if (value === null || value === undefined) {
      return '';
    }

    // Handle booleans
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Handle numbers (but not amount when currency is present)
    if (typeof value === 'number') {
      // Years such as 2027 must not be grouped as "2,027"
      if (currentKey && /year$/i.test(currentKey)) {
        return String(value);
      }
      return value.toLocaleString();
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return this.formatArrayValue(value, currentKey, deals, parsedData);
    }

    // Handle objects (nested)
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }

    // Handle strings
    if (typeof value === 'string') {
      // Check if it looks like a date first
      const dateValue = new Date(value);
      if (!isNaN(dateValue.getTime()) && (value.includes('-') || value.includes('/')) && value.length >= 8) {
        return dateValue.toLocaleDateString();
      }

      // Check if the string is in camelCase and convert to Sentence Case
      if (this.isCamelCase(value)) {
        return this.formatCamelCaseToSentenceCase(value);
      }
    }

    return String(value);
  }

  private formatCurrencyAmount(amount: number, currencyCode: string): string {
    try {
      // Use Intl.NumberFormat for proper currency formatting with accounting style
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode.toUpperCase(),
        currencyDisplay: 'symbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return formatter.format(amount);
    } catch (error) {
      // Fallback if currency code is not recognized
      const symbol = this.getCurrencySymbol(currencyCode);
      return `${symbol}${amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
  }

  private getCurrencySymbol(currencyCode: string): string {
    const symbols: { [key: string]: string } = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'CNY': '¥',
      'INR': '₹',
      'KRW': '₩',
      'MXN': '$',
      'BRL': 'R$',
      'RUB': '₽',
      'ZAR': 'R',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'PLN': 'zł',
      'CZK': 'Kč',
      'HUF': 'Ft',
      'TRY': '₺',
      'ILS': '₪',
      'AED': 'د.إ',
      'SAR': '﷼',
      'THB': '฿',
      'SGD': 'S$',
      'HKD': 'HK$',
      'NZD': 'NZ$'
    };

    return symbols[currencyCode.toUpperCase()] || currencyCode.toUpperCase() + ' ';
  }

  /**
   * Check if a JSON field should be mapped to a specific column name
   * Handle cases where field names don't directly match column names
   */
  private isFieldMappedToColumn(fieldKey: string, columnName: string): boolean {
    const fieldLower = fieldKey.toLowerCase();
    const columnLower = columnName.toLowerCase();

    // Define field-to-column mappings
    const fieldMappings: { [key: string]: string[] } = {
      'loannumber': ['reference name', 'reference', 'loan reference', 'loan ref'],
      'requesttype': ['request type', 'type', 'request kind'],
      'amount': ['amount', 'value', 'sum', 'total'],
      'currency': ['currency', 'curr', 'currency code'],
      'description': ['description', 'desc', 'notes', 'comments'],
      'status': ['status', 'state', 'condition'],
      'priority': ['priority', 'urgency', 'importance'],
      'assignee': ['assignee', 'assigned to', 'owner'],
      'duedate': ['due date', 'deadline', 'target date'],
      'createdate': ['create date', 'created', 'date created'],
      'updatedate': ['update date', 'updated', 'date updated', 'modified'],
      'id': ['id', 'identifier', 'key', 'reference id'],
      'name': ['name', 'title', 'subject'],
      'borrower': ['borrower', 'client', 'customer'],
      'property': ['property', 'asset', 'collateral'],
      'servicer': ['servicer', 'service provider'],
      'lender': ['lender', 'bank', 'institution']
    };

    // Check if the field has any mapped column names
    if (fieldMappings[fieldLower]) {
      return fieldMappings[fieldLower].some(mappedName =>
        columnLower === mappedName ||
        columnLower.includes(mappedName) ||
        mappedName.includes(columnLower)
      );
    }

    return false;
  }

  /**
   * Format array values to show keys and values in a sub-list with round brackets
   */
  private formatArrayValue(array: any[], arrayKey?: string, deals?: any[], parsedData?: any): string {
    // Check if this is a Loan Amounts array
    const isLoanAmountsArray = arrayKey &&
      (arrayKey.toLowerCase() === 'loan amounts' || arrayKey.toLowerCase() === 'loanamounts');

    // Onboarding Tenancy shows this as a bare "val1, val2, val3" list (no parens, no index)
    const isBareSelectedLoanNumbers = arrayKey &&
      arrayKey.toLowerCase() === 'selecteddealloannumbers' &&
      this.isOnboardingTenancyRequest(parsedData);

    // Multi-select Property values (bookName) also render as a bare comma list
    const isBareBookName = arrayKey && arrayKey.toLowerCase() === 'bookname';
    const isBareList = isBareSelectedLoanNumbers || isBareBookName;

    if (array.length === 0) {
      return isBareList ? '' : '()';
    }

    const formattedItems = array.map((item, index) => {
      if (item === null || item === undefined) {
        return (isLoanAmountsArray || isBareList) ? '(empty)' : `${index}: (empty)`;
      }

      // If item is an object, format its key-value pairs
      if (typeof item === 'object' && !Array.isArray(item)) {
        const entries = Object.entries(item);
        if (entries.length === 0) {
          return (isLoanAmountsArray || isBareList) ? '(empty object)' : `${index}: (empty object)`;
        }

        if (isLoanAmountsArray) {
          // Special formatting for Loan Amounts - only show Amount, Borrower Name and Deal Name
          // (Loan Number is intentionally excluded; it duplicates data already shown elsewhere in the grid)
          const formattedEntries = entries
            .filter(([key]) => {
              const keyLower = key.toLowerCase();
              return keyLower === 'amount' || keyLower === 'borrowername' || keyLower === 'borrower name' || keyLower === 'dealnumber';
            })
            .map(([key, val]) => {
              // dealNumber's value is resolved to the deal's display name, so label it accordingly
              const formattedKey = key.toLowerCase() === 'dealnumber' ? 'Deal Name' : this.formatKey(key);
              let formattedValue: string;

              // Special handling for amount values in loan amounts array
              if (key.toLowerCase() === 'amount' && typeof val === 'number') {
                // Use the row's own currency, falling back to the region's default
                const rowCurrency = (item as any).currency || this.featureFlagsService.getDefaultCurrency();
                formattedValue = this.formatCurrencyAmount(val, rowCurrency);
              } else if (key.toLowerCase() === 'dealnumber') {
                // dealNumber is a raw ID copied from the loan lookup — resolve it against the loaded deals list
                formattedValue = UtilitiesService.resolveDealName(val, deals ?? [], this.featureFlagsService.isEu());
              } else {
                formattedValue = this.formatValue(val, undefined);
              }

              return `<strong>${formattedKey}</strong>: ${formattedValue}`;
            });

          return formattedEntries.join(', ');
        } else {
          // Regular object formatting with index
          const formattedEntries = entries.map(([key, val]) => {
            const formattedKey = this.formatKey(key);
            const formattedValue = this.formatValue(val, undefined);
            return `${formattedKey}: ${formattedValue}`;
          });

          return `${index}: {${formattedEntries.join(', ')}}`;
        }
      }

      // For primitive values, format them directly
      const formattedValue = this.formatValue(item, undefined);
      return (isLoanAmountsArray || isBareList) ? formattedValue : `${index}: ${formattedValue}`;
    });

    return isBareList ? formattedItems.join(', ') : `(${formattedItems.join(', ')})`;
  }

  /**
   * Check if a string is in camelCase format
   */
  private isCamelCase(value: string): boolean {
    // Must contain at least one lowercase letter followed by an uppercase letter
    // Should not contain spaces, hyphens, underscores, or start with uppercase
    return /^[a-z][a-zA-Z0-9]*[A-Z][a-zA-Z0-9]*$/.test(value) &&
           !value.includes(' ') &&
           !value.includes('-') &&
           !value.includes('_');
  }

  /**
   * Convert camelCase string to Sentence Case
   * e.g., "firstName" becomes "First name"
   */
  private formatCamelCaseToSentenceCase(value: string): string {
    return value
      .replace(/([A-Z])/g, ' $1') // Insert space before uppercase letters
      .replace(/^./, char => char.toUpperCase()) // Capitalize first letter
      .trim();
  }
}

@Pipe({
  standalone: false,
  name: 'allcapstosentencecase'
})
export class AllCapsToSentenceCasePipe implements PipeTransform {
  transform(value: string): string {
    if (!value || typeof value !== 'string') {
      return value;
    }

    // Check if the string is all uppercase (with possible spaces or underscores)
    if (value === value.toUpperCase()) {
      return value
        .toLowerCase() // Convert to lowercase first
        .replace(/^./, char => char.toUpperCase()) // Capitalize first letter
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();
    }

    return value;
  }
}

@Pipe({
  standalone: false,
  name: 'flexcurrency'
})
export class FlexCurrencyPipe implements PipeTransform {
  constructor(private readonly featureFlagsService: FeatureFlagsService) {}

  transform(value: any, currency?: string): string {
    if (!value && value !== 0) {
      return '';
    }

    const amount = parseFloat(value);
    if (isNaN(amount)) {
      return value?.toString() || '';
    }

    // Use provided currency or default to the region's currency
    const currencyCode = currency || this.featureFlagsService.getDefaultCurrency();

    // Use Intl.NumberFormat for proper currency formatting with accountancy style
    try {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencySign: 'accounting', // Use accounting format (parentheses for negative numbers)
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return formatter.format(amount);
    } catch (error) {
      // Fallback if currency code is invalid
      const fallbackCurrency = this.featureFlagsService.getDefaultCurrency();
      console.warn(`Invalid currency code: ${currencyCode}, using ${fallbackCurrency} as fallback`);
      const fallbackFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: fallbackCurrency,
        currencySign: 'accounting',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return fallbackFormatter.format(amount);
    }
  }
}